import { z } from 'zod';
/**
 * Версии RFB, которые платформа умеет согласовывать (§5.0).
 *
 * Больше в природе и не встречается: 3.8 — последняя опубликованная версия
 * протокола, 3.3 и 3.7 живут в старых серверах. Согласование идёт
 * по минимуму из предложенного сервером и этого списка.
 */
export const RFB_VERSIONS = ['3.3', '3.7', '3.8'];
export const rfbVersionSchema = z.enum(RFB_VERSIONS);
/**
 * Типы безопасности RFB.
 *
 * Реализуются три (§5.0): `NONE`, `VNC_AUTH` и `TIGHT`. Остальные объявлены
 * ради внятного отказа: пользователь должен увидеть, какой именно тип
 * сервер потребовал, а не «Ошибка подключения» — с этим сообщением
 * администратору некуда идти.
 */
export const SecurityType = {
    /** Аутентификации нет. Сервер сразу переходит к инициализации. */
    NONE: 1,
    /** VNC Authentication: DES по вызову, пароль усечён до 8 символов. */
    VNC_AUTH: 2,
    RA2: 5,
    RA2NE: 6,
    /** Через него TightVNC объявляет возможности, включая передачу файлов. */
    TIGHT: 16,
    ULTRA: 17,
    /** Проприетарная аутентификация RealVNC. Не поддерживается. */
    REALVNC_18: 18,
    VENCRYPT: 19,
    /** Apple Screen Sharing. Не поддерживается. */
    APPLE_ARD: 30,
    /** Проприетарная аутентификация RealVNC, второй номер. Не поддерживается. */
    REALVNC_113: 113,
};
/** Типы безопасности, которые шлюз действительно проходит (§5.0). */
export const SUPPORTED_SECURITY_TYPES = [
    SecurityType.NONE,
    SecurityType.VNC_AUTH,
    SecurityType.TIGHT,
];
/**
 * Человеческие названия типов безопасности для сообщения об отказе.
 *
 * §5.0 требует, чтобы сервер с типом 18 или 30 давал сообщение с указанием,
 * какой именно тип не поддерживается. Номер без названия администратору
 * мало что говорит, поэтому в сообщение идёт и то, и другое.
 */
export const SECURITY_TYPE_KEYS = {
    [SecurityType.NONE]: 'vnc.securityType.none',
    [SecurityType.VNC_AUTH]: 'vnc.securityType.vncAuth',
    [SecurityType.RA2]: 'vnc.securityType.ra2',
    [SecurityType.RA2NE]: 'vnc.securityType.ra2ne',
    [SecurityType.TIGHT]: 'vnc.securityType.tight',
    [SecurityType.ULTRA]: 'vnc.securityType.ultra',
    [SecurityType.REALVNC_18]: 'vnc.securityType.realvnc',
    [SecurityType.VENCRYPT]: 'vnc.securityType.vencrypt',
    [SecurityType.APPLE_ARD]: 'vnc.securityType.appleArd',
    [SecurityType.REALVNC_113]: 'vnc.securityType.realvnc',
};
/**
 * Название типа безопасности для подстановки в сообщение об отказе.
 *
 * Возвращается не подпись, а `$t(ключ)`: имена подставляются в текст,
 * который читает человек в браузере, и переводятся вместе с ним. Номера
 * типов, которых платформа не знает, названия не имеют вовсе — там ключ
 * с подстановкой номера.
 */
export function securityTypeName(type) {
    const key = SECURITY_TYPE_KEYS[type];
    return key === undefined
        ? `$t(vnc.securityType.numbered, { "type": ${String(type)} })`
        : `$t(${key})`;
}
/**
 * Кодировки, объявляемые серверу (§5.0).
 *
 * Список закрытый и одинаковый для всех подключений — меняется только
 * порядок, по `preferEncoding`. Шлюз переписывает `SetEncodings` браузера
 * этим списком: иначе предпочтение из настроек узла ни на что не влияло бы,
 * потому что кодировки выбирает клиент, а не сервер.
 */
export const Encoding = {
    RAW: 0,
    COPY_RECT: 1,
    HEXTILE: 5,
    TIGHT: 7,
    ZRLE: 16,
};
/** Псевдокодировки: не способ сжатия, а объявление возможностей клиента. */
export const PseudoEncoding = {
    /** Курсор рисует клиент — без него указатель ползёт с задержкой сети. */
    CURSOR: -239,
    /** Сервер вправе менять размер рабочего стола на ходу. */
    DESKTOP_SIZE: -223,
    /** Признак конца обновления без подсчёта прямоугольников. */
    LAST_RECT: -224,
    /** Обновления без запроса на каждый кадр. */
    CONTINUOUS_UPDATES: -313,
    /**
     * Клиент вправе попросить сервер сменить разрешение рабочего стола.
     *
     * В перечне §5.0 её нет — добавлена ради режима «по размеру окна»
     * (§5.1, задача 10): без неё этот режим ничем не отличался бы
     * от «вписать» и был бы пунктом меню, который ничего не делает.
     * На TightVNC 2.x сервер её не объявляет, и режим остаётся недоступным —
     * интерфейс это показывает, а не притворяется.
     */
    EXTENDED_DESKTOP_SIZE: -308,
};
/** Какую кодировку предлагать серверу первой (§7.6, `prefer_encoding`). */
export const VNC_ENCODING_PREFERENCES = ['tight', 'zrle', 'hextile'];
export const vncEncodingPreferenceSchema = z.enum(VNC_ENCODING_PREFERENCES);
/**
 * Тип VNC-сервера (§7.6).
 *
 * `auto` — определить зондированием. Заданное вручную значение зондирование
 * не затирает: в парке встречаются серверы, которые представляются чужим
 * именем, и слово администратора здесь весомее обнаруженного.
 */
export const VNC_SERVER_KINDS = [
    'auto',
    'tightvnc',
    'ultravnc',
    'tigervnc',
    'realvnc',
    'x11vnc',
    'other',
];
export const vncServerKindSchema = z.enum(VNC_SERVER_KINDS);
/** Как шлюз дотянулся до сервера: через агента площадки или напрямую (§5.1, задача 7). */
export const vncRouteModeSchema = z.enum(['agent', 'direct']);
// — Параметры подключения к узлу ——————————————————————————————
export const nodeVncSchema = z.object({
    nodeId: z.uuid(),
    port: z.number().int(),
    serverKind: vncServerKindSchema,
    viewOnly: z.boolean(),
    preferEncoding: vncEncodingPreferenceSchema,
    detectedKind: vncServerKindSchema.nullable(),
    detectedVersion: z.string().nullable(),
    rfbVersion: rfbVersionSchema.nullable(),
    securityTypes: z.array(z.number().int()).nullable(),
    /** NULL — не зондировали. Отличается от `false` намеренно (§5.0). */
    supportsFileTransfer: z.boolean().nullable(),
    lastProbeAt: z.iso.datetime().nullable(),
    lastProbeError: z.string().nullable(),
});
export const updateNodeVncSchema = z
    .object({
    port: z.number().int().min(1).max(65535),
    serverKind: vncServerKindSchema,
    viewOnly: z.boolean(),
    preferEncoding: vncEncodingPreferenceSchema,
})
    .partial();
// — Зондирование (§5.1, задача 6) ————————————————————————————
/**
 * Что удалось узнать о сервере, не аутентифицируясь.
 *
 * Зондирование доходит до согласования типа безопасности, читает
 * Tight-капабилити и закрывает соединение. Пароль при этом не нужен
 * и не запрашивается — проверить порт и тип сервера должно быть можно
 * до того, как учётные данные заведены.
 */
export const vncProbeResultSchema = z.object({
    /** Дошли ли до TCP-соединения. Всё остальное имеет смысл только при `true`. */
    reachable: z.boolean(),
    rfbVersion: rfbVersionSchema.nullable(),
    detectedKind: vncServerKindSchema.nullable(),
    /** Строка идентификации из Tight-капабилити, например `TightVNC 2.8.85`. */
    detectedVersion: z.string().nullable(),
    securityTypes: z.array(z.number().int()),
    supportsFileTransfer: z.boolean(),
    /** Потребует ли сервер пароль: в предложенном нет типа `None`. */
    authRequired: z.boolean(),
    /** Как дотянулись до сервера — видно, работает ли агент площадки. */
    route: vncRouteModeSchema.nullable(),
    /** Причина, по которой зондирование не удалось. */
    error: z.string().nullable(),
    errorCode: z.string().nullable(),
    at: z.iso.datetime(),
});
// — Сессия (§5.1, задача 5) ——————————————————————————————————
/**
 * Возможности, обнаруженные при установке соединения (§5.0).
 *
 * Интерфейс строится по ним, а не по предположениям: файловый менеджер
 * не показывается вовсе, если сервер не объявил передачу файлов.
 */
export const vncCapabilitiesSchema = z.object({
    fileTransfer: z.boolean(),
    clipboard: z.boolean(),
    desktopResize: z.boolean(),
    viewOnly: z.boolean(),
});
/** Сведения о сервере для диагностической панели (§5.1, задача 14). */
export const vncServerInfoSchema = z.object({
    kind: vncServerKindSchema.nullable(),
    version: z.string().nullable(),
    rfbVersion: rfbVersionSchema.nullable(),
    securityTypes: z.array(z.number().int()),
    /** Кодировка, которую шлюз ставит первой в списке для сервера. */
    preferredEncoding: vncEncodingPreferenceSchema,
    route: vncRouteModeSchema,
    host: z.string(),
    port: z.number().int(),
});
export const vncSessionSchema = z.object({
    sessionId: z.uuid(),
    nodeId: z.uuid(),
    nodeName: z.string(),
    /** Куда открывать WebSocket. Относительный путь — домен тот же (§3.1). */
    wsUrl: z.string(),
    /** Одноразовый, TTL 30 секунд (§10.1). */
    ticket: z.string(),
    expiresAt: z.iso.datetime(),
    capabilities: vncCapabilitiesSchema,
    server: vncServerInfoSchema,
});
/** TTL тикета на сессию, секунды (§10.1). */
export const VNC_TICKET_TTL_SEC = 30;
/**
 * Сколько сессий одновременно разрешено (§5.1, задача 9).
 *
 * Пределы настраиваются переменными окружения; здесь — значения
 * по умолчанию, они же участвуют в сообщении об отказе.
 */
export const VNC_DEFAULT_SESSION_LIMIT_PER_USER = 3;
export const VNC_DEFAULT_SESSION_LIMIT_PER_ORG = 20;
//# sourceMappingURL=vnc.js.map